import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Switch,
  TextField,
  Button,
  CircularProgress,
  Divider,
} from "@mui/material";
import { useNotify } from "react-admin";
import axios from "axios";

export default function AdminSettings() {
  const [loading, setLoading] = useState(true);

  // Existing states
  const [inAppPurchase, setInAppPurchase] = useState(false);
  const [passmark, setPassmark] = useState("");

  // Contact info states
  const [contact, setContact] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
  });

  const notify = useNotify();
  const token = localStorage.getItem("jwtToken");
  const BASE_URL = process.env.REACT_APP_API_URL;

  // Fetch settings + contact info
  useEffect(() => {
    Promise.all([
      axios.get(`${BASE_URL}/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      axios.get(`${BASE_URL}/contactinfo`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    ])
      .then(([settingsRes, contactRes]) => {
        setInAppPurchase(settingsRes.data.in_app_purchase);
        setPassmark(settingsRes.data.passmark);
        setContact(contactRes.data.data);
        setLoading(false);
      })
      .catch(() => {
        notify("Failed to load admin data", { type: "warning" });
        setLoading(false);
      });
  }, []);

  // Existing handlers
  const handleToggle = async () => {
    try {
      const res = await axios.post(
        `${BASE_URL}/toggle_in_app_purchase`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setInAppPurchase(res.data.in_app_purchase);
      notify("In-app purchase updated", { type: "info" });
    } catch {
      notify("Failed to update in-app purchase", { type: "warning" });
    }
  };

  const handlePassmarkUpdate = async () => {
    try {
      await axios.post(
        `${BASE_URL}/set_passmark`,
        { passmark: parseInt(passmark) },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      notify("Passmark updated", { type: "info" });
    } catch {
      notify("Failed to update passmark", { type: "warning" });
    }
  };

  // ✅ Contact info update
  const handleContactUpdate = async () => {
    try {
      await axios.put(`${BASE_URL}/contactinfo`, contact, {
        headers: { Authorization: `Bearer ${token}` },
      });
      notify("Contact info updated successfully", { type: "info" });
    } catch {
      notify("Failed to update contact info", { type: "warning" });
    }
  };

  if (loading) return <CircularProgress />;

  return (
    <div style={{ padding: "2rem" }}>
      <Card sx={{ maxWidth: 600, margin: "auto", borderRadius: "1rem" }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            ⚙️ App Settings
          </Typography>

          {/* In-app purchase */}
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Typography>In-App Purchase</Typography>
            <Switch checked={inAppPurchase} onChange={handleToggle} />
          </div>

          <Divider sx={{ my: 2 }} />

          {/* Passmark */}
          <Typography gutterBottom>Passmark</Typography>
          <TextField
            type="number"
            value={passmark}
            onChange={(e) => setPassmark(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
          <Button variant="contained" onClick={handlePassmarkUpdate}>
            Update Passmark
          </Button>

          <Divider sx={{ my: 3 }} />

          {/* 🔥 Contact Info Editor */}
          <Typography variant="h6" gutterBottom>
            📞 Contact Information
          </Typography>

          <TextField
            label="Company Name"
            value={contact.name}
            onChange={(e) => setContact({ ...contact, name: e.target.value })}
            fullWidth
            sx={{ mb: 2 }}
          />

          <TextField
            label="Phone"
            value={contact.phone}
            onChange={(e) => setContact({ ...contact, phone: e.target.value })}
            fullWidth
            sx={{ mb: 2 }}
          />

          <TextField
            label="Email"
            value={contact.email}
            onChange={(e) => setContact({ ...contact, email: e.target.value })}
            fullWidth
            sx={{ mb: 2 }}
          />

          <TextField
            label="Address"
            value={contact.address}
            onChange={(e) =>
              setContact({ ...contact, address: e.target.value })
            }
            fullWidth
            multiline
            rows={3}
            sx={{ mb: 2 }}
          />

          <Button
            variant="contained"
            color="success"
            onClick={handleContactUpdate}
          >
            Save Contact Info
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
